import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import type {
  CreateConversationRequest,
  CreateConversationResponse,
  GetChatHistoryResponse,
  SendMessageRequest,
  SendMessageResponse,
} from '@/api/schema/data-contracts';
import { useApi } from '@/context/api';
import { useAuth } from '@/context/auth';
import type { AdvisoryStructure } from '@/utils/advisoryStructure';

import type { MutationProps } from '../types';
import type { GetAiAdvisoryResponseResponse, SendMessageAsyncResponse } from './poll';
import { describeStartFailure, interpretPollResponse } from './poll';
import {
  EMPTY_STREAM_RECONCILE_MS,
  hasUserMessageWithContent,
  pickLatestAssistantMessage,
  RECONCILE_MAX_DURATION_MS,
  type ReconcileMessage,
  reconcilePollDelayMs,
  SSE_FIRST_RESPONSE_TIMEOUT_MS,
} from './reconcile';

// EWM-652 / EWM-866: when a corporate proxy (e.g. LBBW HASE/FONTUS) severs the SSE connection
// before the terminal `done` event arrives, the server keeps generating on an independent
// token (for the streaming budget in SendMessageStreamCommandHandler, then via the reaper) and persists
// the response. The client reconciles by polling chat-history until that persisted assistant
// message shows up. The poll window (RECONCILE_MAX_DURATION_MS in ./reconcile) now covers the
// full server generation cap so a long reasoning answer is still recovered instead of
// stranding the user on an empty conversation. Timing/selection live in ./reconcile so they
// can be unit-tested. The loop is aborted on page unmount (see AIAdvisoryPage) so it can't
// outlive the view. Tier-2 (durable background generation + resumable poll) would replace
// polling entirely — see EWM-866 phase 2.

// EWM-1068: carries the HTTP status of a failed advisory-structure resolve so the convert page can
// separate "the extraction did not finish, retrying works" (503) from a genuine fault.
export class AdvisoryStructureError extends Error {
  public readonly status: number;

  public constructor(status: number) {
    super(`Failed to resolve advisory structure: HTTP ${status}`);
    this.name = 'AdvisoryStructureError';
    this.status = status;
  }
}

// How many consecutive transient poll failures (429/408/5xx) to ride out before telling the user.
// At the escalating poll cadence this is ~1 minute of retrying.
/** Chat history, or undefined if it could not be read. Never throws. */
const listChatHistorySafely = async (
  listChatHistory: (params: { ConversationId: string; Page: number; PageSize: number }) => Promise<{
    data: { messages: ReconcileMessage[] };
  }>,
  conversationId: string,
) => {
  try {
    const { data } = await listChatHistory({
      ConversationId: conversationId,
      Page: 1,
      PageSize: 50,
    });
    return data.messages;
  } catch {
    return undefined;
  }
};

/**
 * Wait for the answer the server is already generating. No ceiling: the server persists the
 * result on its own token and the reaper backstops it, so giving up here could only invent a
 * failure. Ends when the answer lands or the caller aborts.
 */
const waitForPersistedAnswer = async (
  listChatHistory: (params: { ConversationId: string; Page: number; PageSize: number }) => Promise<{
    data: { messages: ReconcileMessage[] };
  }>,
  conversationId: string,
  signal: AbortSignal,
  onFound: (answer: ReconcileMessage) => void,
) => {
  const startedAt = Date.now();
  while (!signal.aborted) {
    const messages = await listChatHistorySafely(listChatHistory, conversationId);
    const answer = messages ? pickLatestAssistantMessage(messages) : undefined;
    if (answer) {
      onFound(answer);
      return;
    }
    await new Promise((resolve) => {
      setTimeout(resolve, reconcilePollDelayMs(Date.now() - startedAt));
    });
  }
};

/**
 * One-shot check for an answer the server already persisted. Used before showing the user any
 * error: the generation outlives the client connection (EWM-652) and the reaper persists it
 * independently, so "my request failed" very often means "the answer is already in history".
 */
const findPersistedAnswer = async (
  listChatHistory: (params: { ConversationId: string; Page: number; PageSize: number }) => Promise<{
    data: { messages: ReconcileMessage[] };
  }>,
  conversationId: string,
) => {
  try {
    const { data } = await listChatHistory({
      ConversationId: conversationId,
      Page: 1,
      PageSize: 50,
    });
    return pickLatestAssistantMessage(data.messages);
  } catch {
    return undefined;
  }
};

// Temporary types until API schema is regenerated
export interface ExportConversationRequest {
  conversationId: string;
  format: string;
  includeMetadata?: boolean;
  includeTimestamps?: boolean;
}

export interface ExportConversationResponse {
  fileName: string;
  contentType: string;
  fileContent: number[];
  fileSizeBytes: number;
  exportedAt: string;
}

// EWM-605: Convert AI Advisory conversation → Estimation draft
export interface ConvertAdvisoryToEstimationRequest {
  selectedWbsIds: string[];
  selectedAssumptionIds: string[];
  projectName: string;
  projectDescription?: string | null;
  aiAdvisoryDocumentLink?: string | null;
  // EWM-957: the project type picked on the convert page, which decides the estimation model.
  // Omitted falls back to the seeded "AI Advisory" model this flow used to force.
  projectTypeId?: string | null;
}

// EWM-957: a project type on the convert page's picker. Every project type in the tenant is listed;
// the ones whose model cannot hold an import come back with isSelectable false and the reason, so a
// user can tell "not set up for this" from "does not exist".
export interface AdvisoryConvertTarget {
  projectTypeId: string;
  name: string;
  estimationModelId: string | null;
  isAdvisoryDefault: boolean;
  isSelectable: boolean;
  // Stable codes, localized on this side: NoPublishedModel, NoItemGroup, NoPersonDaysUnit,
  // NoThreePointMethodology. Non-empty means the option is offered but disabled.
  missingRequirements: string[];
  // Selectable, but something downstream will not work. NoCurrency: the cost tabs cannot save.
  warnings: string[];
}

export interface ConvertAdvisoryToEstimationResponse {
  estimationId: string;
  projectId: string;
  projectName: string;
  itemCount: number;
  assumptionCount: number;
}

export interface ConversationItem {
  conversationId: string;
  title: string;
  createdAt: string;
  lastMessageAt?: string | null;
  /** EWM-521: optional sidebar folder grouping. null = unfoldered (top-level). */
  folderId?: string | null;
  /** EWM-534: false when the conversation was shared with the current user by someone else. */
  isOwner: boolean;
}

// EWM-521: Conversation folder types
export interface ConversationFolderItem {
  folderId: string;
  name: string;
  createdAt: string;
  conversationCount: number;
}

export interface GetConversationFoldersResponse {
  folders: ConversationFolderItem[];
}

export interface CreateConversationFolderResponse {
  folderId: string;
  name: string;
  createdAt: string;
}

export interface GetConversationsResponse {
  conversations: ConversationItem[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

// SSE Streaming types
export interface StreamChunkEvent {
  content: string;
}

export interface StreamUserMessageEvent {
  userMessageId: string;
}

export interface StreamDoneEvent {
  assistantMessageId: string;
  processingTime: number;
  // EWM-1085: the answer was persisted (so this is still "done", not "error" - the content on
  // screen is real and must not be replaced), but the generation was cut short partway through -
  // either a service failure or the server-side generation-timeout cap, which truncates the
  // answer silently. Without this the interruption was only ever visible in the server log.
  degraded?: boolean;
  // EY pentest 26-B3FC-65B76: set only when the server replaced an answer that disclosed the
  // assistant's configuration. It supersedes the chunks already shown.
  content?: string | null;
}

export interface StreamErrorEvent {
  message: string;
}

// EWM-968: the server has always emitted these two while it works - a reasoning delta and a tool
// call's progress - and the client dropped both on `default: break`. A reasoning-heavy answer
// therefore showed an empty bubble for minutes (measured: one 200 response took 6m40s with no
// visible output). They carry no answer text, so they drive a status line, never message content.
export interface StreamThinkingEvent {
  content: string;
}

export interface StreamToolEvent {
  name: string;
  status: string;
}

export type StreamEvent =
  | { type: 'userMessage'; data: StreamUserMessageEvent }
  | { type: 'chunk'; data: StreamChunkEvent }
  | { type: 'thinking'; data: StreamThinkingEvent }
  | { type: 'tool'; data: StreamToolEvent }
  | { type: 'done'; data: StreamDoneEvent }
  | { type: 'error'; data: StreamErrorEvent };

/**
 * What the assistant is doing before any answer text exists. Deliberately not a display string:
 * the transport layer stays free of i18n, and the page maps this to localised copy.
 */
export type StreamActivity = { kind: 'thinking' } | { kind: 'tool'; name: string };

export interface StreamingCallbacks {
  onUserMessage?: (userMessageId: string) => void;
  onChunk?: (content: string) => void;
  // `content` is the final answer. REQUIRED on the poll path, where onChunk never fires:
  // omitting it left the assistant bubble empty until a page reload (EWM-605 debug).
  // `degraded` (EWM-1085): the persisted answer is real, but generation was cut short partway
  // through - true only on the SSE path, where the server can tell the difference.
  onDone?: (
    assistantMessageId: string,
    processingTime: number,
    content?: string,
    degraded?: boolean,
  ) => void;
  onError?: (error: string) => void;
  /**
   * EWM-968: progress while the answer does not exist yet. Fires on reasoning and tool-call
   * events, which arrive within seconds, so the user sees movement instead of a blank bubble.
   */
  onActivity?: (activity: StreamActivity) => void;
  /**
   * EWM-652: fired when the SSE stream closed before a terminal `done` event (proxy drop),
   * but the full response was recovered from the server's persisted chat history. Carries
   * the complete content so the UI can replace any partial text instead of freezing.
   */
  onRecovered?: (assistantMessageId: string, content: string) => void;
}

export interface UseStreamMessageReturn {
  /**
   * Resolves 'not-established' when the transport never carried a byte and nothing was
   * recovered - the caller should retry on the polling path. Resolves void otherwise
   * (completed, recovered, user-cancelled, or a terminal failure already surfaced).
   */
  sendStreamMessage: (conversationId: string, content: string) => Promise<'not-established' | void>;
  isStreaming: boolean;
  cancel: () => void;
  error: string | null;
  accumulatedText: string;
  resetAccumulatedText: () => void;
}

// The conversations list endpoint caps PageSize at 100 (backend GetConversationsRequest [Range(1,100)]).
export const CONVERSATIONS_PAGE_SIZE = 100;
// Safety cap so a runaway pager can never loop forever — 20 pages = 2000 conversations.
export const MAX_CONVERSATION_PAGES = 20;

/**
 * Pages through the entire conversation list, accumulating every conversation.
 *
 * The sidebar renders folders from a separate, unbounded /folders query but fills each folder
 * from this list. Foldered conversations are frequently the *older* ones (their LastMessageAt
 * lags freshly-created scratch conversations), so a single most-recent page can omit them all
 * and every folder renders empty despite a non-zero count badge. Fetching all pages keeps the
 * folder contents in sync with their counts.
 *
 * Extracted as a pure function (fetchPage injected) so the paging logic is unit-testable without
 * a live API. Fails loud — warns rather than silently truncating — if a tenant ever exceeds the cap.
 */
export const collectAllConversations = async (
  fetchPage: (page: number, pageSize: number) => Promise<GetConversationsResponse>,
): Promise<GetConversationsResponse> => {
  const conversations: ConversationItem[] = [];
  let last: GetConversationsResponse | null = null;
  for (let page = 1; page <= MAX_CONVERSATION_PAGES; page += 1) {
    const data = await fetchPage(page, CONVERSATIONS_PAGE_SIZE);
    conversations.push(...data.conversations);
    last = data;
    if (!data.hasNextPage) break;
  }
  if (last?.hasNextPage) {
    // Fail loud rather than silently truncate — surfaces if a tenant exceeds the page cap.
    console.error(
      `AI Advisory sidebar: conversation list truncated at ${MAX_CONVERSATION_PAGES} pages ` +
        `(${conversations.length}/${last.totalCount}). Raise MAX_CONVERSATION_PAGES.`,
    );
  }
  return {
    conversations,
    totalCount: last?.totalCount ?? conversations.length,
    page: 1,
    pageSize: conversations.length,
    totalPages: 1,
    hasNextPage: false,
    hasPreviousPage: false,
  };
};

export const useAiAdvisoryQueryActions = () => {
  const { auth } = useAuth();
  // sendStreamMessage (both the SSE and the poll variant) is memoized with an empty dep array to
  // keep a stable identity for callers, so it must not close over `auth`: after a token refresh it
  // would keep sending the old bearer, and the poll loop used to swallow the resulting 401.
  const authRef = useRef(auth);
  // EWM-901: assigned in an effect rather than during render — writing a ref while rendering
  // is not allowed (react-hooks/refs). Every reader is inside a memoized callback invoked from
  // a handler or the poll timer, i.e. always after commit, so they still see the latest token.
  useEffect(() => {
    authRef.current = auth;
  });
  const {
    aiAdvisoryController: {
      v1AiAdvisoryConversationsCreate,
      v1AiAdvisoryConversationsMessagesCreate,
      v1AiAdvisoryChatHistoryList,
    },
  } = useApi();

  const queryClient = useQueryClient();

  const useCreateConversation = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['create-ai-advisory-conversation'],
      mutationFn: async (
        request: CreateConversationRequest,
      ): Promise<CreateConversationResponse> => {
        const { data } = await v1AiAdvisoryConversationsCreate(request);
        return data;
      },
      onSuccess: async () => {
        // Invalidate chat history and conversations to refresh the lists
        await queryClient.invalidateQueries({ queryKey: ['ai-advisory-chat-history'] });
        await queryClient.invalidateQueries({ queryKey: ['ai-advisory-conversations'] });
      },
    });

  const useSendMessage = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['send-ai-advisory-message'],
      mutationFn: async ({
        conversationId,
        request,
      }: {
        conversationId: string;
        request: SendMessageRequest;
      }): Promise<SendMessageResponse> => {
        const { data } = await v1AiAdvisoryConversationsMessagesCreate(conversationId, request);
        return data;
      },
      onSuccess: async () => {
        // Invalidate chat history and conversations to refresh the lists
        await queryClient.invalidateQueries({ queryKey: ['ai-advisory-chat-history'] });
        await queryClient.invalidateQueries({ queryKey: ['ai-advisory-conversations'] });
      },
    });

  const useGetChatHistory = (params?: {
    Page?: number;
    PageSize?: number;
    ConversationId?: string;
  }) =>
    useQuery({
      enabled: Boolean(auth),
      queryKey: ['ai-advisory-chat-history', params],
      queryFn: async (): Promise<GetChatHistoryResponse> => {
        const { data } = await v1AiAdvisoryChatHistoryList(params);
        return data;
      },
    });

  // EWM-605: what this conversation can convert into, decided server-side. The sentinel fast path
  // and the strict json_schema fallback both live behind this endpoint, so an advisory without the
  // hidden block is still convertible. 204 → null (genuinely nothing to import).
  // Direct fetch, matching useGetConversations below, until the API schema is regenerated.
  // EWM-1086: the AI Advisory page passes the latest assistant message id so a new reply
  // re-resolves; the server caches per message, so the convert page then hits the warm cache.
  const useGetAdvisoryStructure = (conversationId: string, messageId?: string) =>
    useQuery({
      enabled: Boolean(auth) && Boolean(conversationId),
      queryKey: ['ai-advisory-structure', conversationId, ...(messageId ? [messageId] : [])],
      // The fallback is an LLM call, so don't re-run it on window focus / remount.
      staleTime: 5 * 60 * 1000,
      retry: false,
      queryFn: async (): Promise<AdvisoryStructure | null> => {
        const response = await fetch(
          `/api/v1/ai-advisory/conversations/${conversationId}/advisory-structure`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${auth?.accessToken}`,
            },
          },
        );
        if (response.status === 204) return null;
        if (!response.ok) {
          // EWM-1068: the status has to survive to the error overlay. A truncated/timed-out/
          // throttled extraction now answers 503, and that is the one failure the user should be
          // told to retry - the overlay cannot tell it apart from a real fault without this.
          throw new AdvisoryStructureError(response.status);
        }
        return (await response.json()) as AdvisoryStructure;
      },
    });

  // Loads every conversation (paging through all pages) so the sidebar's folders render their
  // contents — a single page sorted by recency can omit all foldered (older) conversations.
  const useGetConversations = () =>
    useQuery({
      enabled: Boolean(auth),
      queryKey: ['ai-advisory-conversations', 'all'],
      queryFn: (): Promise<GetConversationsResponse> =>
        collectAllConversations(async (page, pageSize) => {
          const url = `/api/v1/ai-advisory/conversations?Page=${page}&PageSize=${pageSize}`;
          const response = await fetch(url, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${auth?.accessToken}`,
            },
          });
          if (!response.ok) {
            throw new Error(`Failed to fetch conversations: ${response.statusText}`);
          }
          return (await response.json()) as GetConversationsResponse;
        }),
    });

  const useExportConversation = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['export-ai-advisory-conversation'],
      mutationFn: async ({
        conversationId,
        request,
      }: {
        conversationId: string;
        request: ExportConversationRequest;
      }): Promise<ExportConversationResponse> => {
        // Temporary direct fetch until API schema is regenerated
        const response = await fetch(`/api/v1/ai-advisory/conversations/${conversationId}/export`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth?.accessToken}`,
          },
          body: JSON.stringify(request),
        });

        if (!response.ok) {
          throw new Error(`Export failed: ${response.statusText}`);
        }

        return await response.json();
      },
    });

  // EWM-957: which project types this tenant can import an advisory into. Direct fetch, matching
  // useGetAdvisoryStructure above, until the API schema is regenerated.
  const useGetAdvisoryConvertTargets = () =>
    useQuery({
      enabled: Boolean(auth),
      queryKey: ['ai-advisory-convert-targets'],
      staleTime: 5 * 60 * 1000,
      queryFn: async (): Promise<AdvisoryConvertTarget[]> => {
        const response = await fetch('/api/v1/ai-advisory/convert-targets', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth?.accessToken}`,
          },
        });
        if (!response.ok) {
          throw new Error(`Failed to load convert targets: HTTP ${response.status}`);
        }
        return (await response.json()) as AdvisoryConvertTarget[];
      },
    });

  const useConvertConversationToEstimation = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['convert-ai-advisory-conversation-to-estimation'],
      mutationFn: async ({
        conversationId,
        request,
      }: {
        conversationId: string;
        request: ConvertAdvisoryToEstimationRequest;
      }): Promise<ConvertAdvisoryToEstimationResponse> => {
        const response = await fetch(
          `/api/v1/ai-advisory/conversations/${conversationId}/convert-to-estimation`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${auth?.accessToken}`,
            },
            body: JSON.stringify(request),
          },
        );

        if (!response.ok) {
          const contentType = response.headers.get('content-type') ?? '';
          let detail = response.statusText;
          if (contentType.includes('application/json')) {
            try {
              const body = (await response.json()) as { detail?: string; errorCode?: string };
              if (body.detail) detail = body.detail;
            } catch {
              // fall through with statusText
            }
          }
          throw new Error(`Convert to estimation failed: ${detail}`);
        }

        return await response.json();
      },
    });

  const useShareConversation = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['share-ai-advisory-conversation'],
      mutationFn: async ({
        conversationId,
        email,
      }: {
        conversationId: string;
        email: string;
      }): Promise<void> => {
        const response = await fetch(`/api/v1/ai-advisory/conversations/${conversationId}/share`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth?.accessToken}`,
          },
          body: JSON.stringify({ email }),
        });

        if (!response.ok) {
          const contentType = response.headers.get('content-type') ?? '';
          let detail = response.statusText;
          if (contentType.includes('application/json')) {
            const body = await response.json().catch(() => null);
            detail = body?.detail || body?.title || detail;
          } else {
            detail = await response.text().catch(() => detail);
          }
          throw new Error(`Share failed: ${response.status} ${detail}`);
        }
      },
    });

  /**
   * Custom hook for streaming AI messages using Server-Sent Events (SSE)
   * This hook handles the SSE connection, parses events, and manages streaming state
   */
  const useStreamMessage = (callbacks?: StreamingCallbacks): UseStreamMessageReturn => {
    const [isStreaming, setIsStreaming] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [accumulatedText, setAccumulatedText] = useState('');
    const abortControllerRef = useRef<AbortController | null>(null);
    // Store callbacks in a ref so the running async sendStreamMessage
    // always calls the latest callbacks, not stale ones from when it was created.
    const callbacksRef = useRef(callbacks);
    callbacksRef.current = callbacks;

    const resetAccumulatedText = useCallback(() => {
      setAccumulatedText('');
    }, []);

    const cancel = useCallback(() => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
        setIsStreaming(false);
      }
    }, []);

    const parseSSEEvent = useCallback((eventString: string): StreamEvent | null => {
      const lines = eventString.trim().split('\n');
      let eventType = 'message';
      let data = '';

      for (const line of lines) {
        if (line.startsWith('event:')) {
          eventType = line.substring(6).trim();
        } else if (line.startsWith('data:')) {
          data = line.substring(5).trim();
        }
      }

      if (!data) return null;

      try {
        const parsedData = JSON.parse(data);
        return { type: eventType as StreamEvent['type'], data: parsedData };
      } catch (e) {
        console.error('Failed to parse SSE event data:', e);
        return null;
      }
    }, []);

    const sendStreamMessage = useCallback(
      async (conversationId: string, content: string) => {
        // Reset state
        setError(null);
        setAccumulatedText('');
        setIsStreaming(true);

        // Create new abort controller for this request
        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        let receivedTerminalEvent = false;
        let receivedAnyContent = false;

        // EWM-652 / EWM-866: poll the server's persisted chat history for this conversation
        // until the assistant message appears, then hand the full content back via
        // onRecovered. Used when the SSE connection was severed before the terminal `done`
        // event arrived. Polls for up to RECONCILE_MAX_DURATION_MS (covering the server's
        // streaming budget and the reaper window) with an escalating interval, and bails immediately if the
        // stream is aborted (user cancel or page unmount).
        const reconcilePersistedResponse = async (
          maxDurationMs: number = RECONCILE_MAX_DURATION_MS,
        ): Promise<boolean> => {
          const startedAt = Date.now();
          while (Date.now() - startedAt < maxDurationMs) {
            if (abortController.signal.aborted) return false;
            try {
              const { data } = await v1AiAdvisoryChatHistoryList({
                ConversationId: conversationId,
                Page: 1,
                PageSize: 50,
              });
              const assistant = pickLatestAssistantMessage(data.messages);
              if (assistant) {
                setAccumulatedText(assistant.content);
                callbacksRef.current?.onRecovered?.(assistant.messageId, assistant.content);
                return true;
              }
            } catch {
              // transient (network/auth) — keep polling
            }
            const delay = reconcilePollDelayMs(Date.now() - startedAt);
            await new Promise((resolve) => {
              setTimeout(resolve, delay);
            });
          }
          return false;
        };

        // Abandon streaming if the response never even begins - see SSE_FIRST_RESPONSE_TIMEOUT_MS.
        let abandonedBeforeResponse = false;
        const firstResponseTimer = setTimeout(() => {
          abandonedBeforeResponse = true;
          abortController.abort();
        }, SSE_FIRST_RESPONSE_TIMEOUT_MS);

        try {
          const streamUrl = `/api/v1/ai-advisory/conversations/${conversationId}/messages/stream`;

          const response = await fetch(streamUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${authRef.current?.accessToken}`,
            },
            body: JSON.stringify({ content }),
            signal: abortController.signal,
          });

          // A network that forbids SSE outright (proxy, WAF, content-type rewrite) fails here,
          // before a single byte of the stream. That is not an error to show anyone - it is a
          // signal to use the polling path instead, so report it and let the caller decide.
          clearTimeout(firstResponseTimer);

          if (!response.ok || !response.body) {
            return 'not-established';
          }

          // Read the stream
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';

          while (true) {
            const { done, value } = await reader.read();

            if (done) {
              break;
            }

            // Decode the chunk and add to buffer
            const chunk = decoder.decode(value, { stream: true });
            buffer += chunk;

            // Process complete events (separated by double newline)
            const events = buffer.split('\n\n');
            // Keep the last incomplete event in the buffer
            buffer = events.pop() || '';

            for (const eventString of events) {
              if (!eventString.trim()) continue;

              const event = parseSSEEvent(eventString);
              if (!event) continue;

              switch (event.type) {
                case 'userMessage': {
                  const { userMessageId } = event.data;
                  callbacksRef.current?.onUserMessage?.(userMessageId);
                  break;
                }
                case 'chunk': {
                  const { content: chunkContent } = event.data;
                  setAccumulatedText((prev) => prev + chunkContent);
                  receivedAnyContent = true;
                  callbacksRef.current?.onChunk?.(chunkContent);
                  break;
                }
                case 'thinking': {
                  callbacksRef.current?.onActivity?.({ kind: 'thinking' });
                  break;
                }
                case 'tool': {
                  const { name } = event.data;
                  callbacksRef.current?.onActivity?.({ kind: 'tool', name });
                  break;
                }
                case 'done': {
                  const {
                    assistantMessageId,
                    processingTime,
                    degraded,
                    content: replacement,
                  } = event.data;
                  receivedTerminalEvent = true;
                  callbacksRef.current?.onDone?.(
                    assistantMessageId,
                    processingTime,
                    replacement ?? undefined,
                    degraded,
                  );
                  break;
                }
                case 'error': {
                  const { message } = event.data;
                  receivedTerminalEvent = true;
                  setError(message);
                  callbacksRef.current?.onError?.(message);
                  break;
                }
                default: {
                  // Ignore unknown event types
                  break;
                }
              }
            }
          }

          // Stream closed. If we never saw a terminal `done`/`error` event and the user did
          // not cancel, the connection was severed mid-flight (EWM-652) — recover the
          // server-persisted response instead of leaving a frozen partial message.
          if (!receivedTerminalEvent && !abortController.signal.aborted) {
            const recovered = await reconcilePersistedResponse(
              receivedAnyContent ? RECONCILE_MAX_DURATION_MS : EMPTY_STREAM_RECONCILE_MS,
            );
            if (!recovered) {
              // The server persists on its own independent token (EWM-652), so the answer is
              // very likely still coming. Say nothing and let the caller keep waiting on the
              // polling path instead of blaming the user's connection.
              return 'not-established';
            }
          }
        } catch (err: unknown) {
          if (err instanceof Error) {
            if (err.name === 'AbortError') {
              // Our own deadline, not the user: fall back to polling without a word.
              if (abandonedBeforeResponse) return 'not-established';
              console.error('Stream was cancelled by user', err);
            } else {
              // Network error mid-stream (e.g. proxy reset). The server may still have
              // completed and persisted the response — try to recover it before erroring.
              const recovered = !abortController.signal.aborted
                ? await reconcilePersistedResponse()
                : false;
              if (!recovered) {
                // Same reasoning as above: hand back a fallback signal, stay quiet.
                return 'not-established';
              }
            }
          } else {
            const errorMessage = 'An unknown error occurred';
            setError(errorMessage);
            callbacksRef.current?.onError?.(errorMessage);
          }
        } finally {
          clearTimeout(firstResponseTimer);
          setIsStreaming(false);
          abortControllerRef.current = null;

          // Invalidate chat history to ensure UI is in sync with backend
          await queryClient.invalidateQueries({ queryKey: ['ai-advisory-chat-history'] });
          await queryClient.invalidateQueries({ queryKey: ['ai-advisory-conversations'] });
        }
      },
      [parseSSEEvent],
    );

    return {
      sendStreamMessage,
      isStreaming,
      cancel,
      error,
      accumulatedText,
      resetAccumulatedText,
    };
  };

  /**
   * EWM-866 (ADR-003): enqueue-don't-hold path — starts generation via `POST .../messages/async`
   * (returns immediately) and polls `GET .../responses/{responseId}` until it resolves. Final-only,
   * not progressive: `onChunk` never fires, the full answer arrives once via `onDone`. Exposes the
   * same shape as {@link useAiAdvisoryQueryActions.useStreamMessage} (the untouched SSE fast-path,
   * kept as a one-file rollback) so callers don't need to change beyond swapping the hook.
   */
  const usePollMessage = (callbacks?: StreamingCallbacks): UseStreamMessageReturn => {
    const [isStreaming, setIsStreaming] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [accumulatedText, setAccumulatedText] = useState('');
    const abortControllerRef = useRef<AbortController | null>(null);
    const callbacksRef = useRef(callbacks);
    callbacksRef.current = callbacks;

    const resetAccumulatedText = useCallback(() => {
      setAccumulatedText('');
    }, []);

    const cancel = useCallback(() => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
        setIsStreaming(false);
      }
    }, []);

    const sendStreamMessage = useCallback(async (conversationId: string, content: string) => {
      setError(null);
      setAccumulatedText('');
      setIsStreaming(true);

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      try {
        // Is this a fallback for a turn the server already took? The stream endpoint stores the
        // user's message before generating, so its presence means an attempt is already running.
        // Starting a second one here would produce two answers to one question (Ana, PR 1929);
        // instead, wait for the original to land.
        const history = await listChatHistorySafely(v1AiAdvisoryChatHistoryList, conversationId);
        if (history && hasUserMessageWithContent(history, content)) {
          const existing = pickLatestAssistantMessage(history);
          if (existing) {
            setAccumulatedText(existing.content);
            callbacksRef.current?.onDone?.(existing.messageId, 0, existing.content);
            return;
          }
          await waitForPersistedAnswer(
            v1AiAdvisoryChatHistoryList,
            conversationId,
            abortController.signal,
            (answer) => {
              setAccumulatedText(answer.content);
              callbacksRef.current?.onDone?.(answer.messageId, 0, answer.content);
            },
          );
          return;
        }

        const startResponse = await fetch(
          `/api/v1/ai-advisory/conversations/${conversationId}/messages/async`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${authRef.current?.accessToken}`,
            },
            body: JSON.stringify({ content }),
            signal: abortController.signal,
          },
        );

        if (!startResponse.ok) {
          // statusText is empty over HTTP/2, which is how this surfaced to users as the
          // uninformative "Failed to start AI Advisory generation: ." Include the status code.
          const problem = (await startResponse.json().catch(() => null)) as {
            errorCode?: string;
            detail?: string;
          } | null;
          throw new Error(
            describeStartFailure(startResponse.status, startResponse.statusText, problem),
          );
        }

        const started = (await startResponse.json()) as SendMessageAsyncResponse;
        callbacksRef.current?.onUserMessage?.(started.userMessageId);

        // Same escalating cadence as the phase-1 reconcile poll (fast at first, easing off for
        // the long reasoning tail), bounded by the server's own GenerationTimeout window.
        // No deadline. The server keeps generating on its own token and the reaper persists the
        // answer regardless, so a client-side timeout could only ever invent a failure that had
        // not happened. We keep waiting while the tab is open; if the user leaves, the answer is
        // stored anyway and appears on reload.
        const startedAt = Date.now();
        for (;;) {
          if (abortController.signal.aborted) return;

          const pollResponse = await fetch(
            `/api/v1/ai-advisory/conversations/${conversationId}/responses/${started.responseId}`,
            {
              method: 'GET',
              headers: { Authorization: `Bearer ${authRef.current?.accessToken}` },
              signal: abortController.signal,
            },
          );

          if (!pollResponse.ok) {
            // 429/5xx/408 are worth riding out; anything else (401 after a token rotation, 403,
            // 404 for a reaped response) will never resolve, and silently polling through it for
            // 16 minutes then blaming a timeout is how a hard failure got reported as slowness.
            const isTransient =
              pollResponse.status === 429 ||
              pollResponse.status === 408 ||
              pollResponse.status >= 500;

            if (!isTransient) {
              // 401 after a token rotation, 403, or 404 for a reaped response: this poll will
              // never resolve. The answer may still have been persisted server-side, so look in
              // chat history before saying anything to the user.
              const persisted = await findPersistedAnswer(
                v1AiAdvisoryChatHistoryList,
                conversationId,
              );
              if (persisted) {
                setAccumulatedText(persisted.content);
                callbacksRef.current?.onDone?.(persisted.messageId, 0, persisted.content);
                return;
              }
              const message =
                'The response could not be retrieved. It may still be processing - please reload the conversation in a moment.';
              setError(message);
              callbacksRef.current?.onError?.(message);
              return;
            }

            // Transient: ride it out indefinitely. Counting failures and then declaring the
            // service dead produced a red banner for what was usually a single proxy hiccup.
          } else {
            const polled = (await pollResponse.json()) as GetAiAdvisoryResponseResponse;
            const outcome = interpretPollResponse(polled);

            if (outcome.kind === 'done') {
              setAccumulatedText(outcome.content);
              callbacksRef.current?.onDone?.(
                outcome.assistantMessageId,
                outcome.processingTime,
                outcome.content,
              );
              return;
            }

            if (outcome.kind === 'failed') {
              setError(outcome.message);
              callbacksRef.current?.onError?.(outcome.message);
              return;
            }
            // 'continue' — Queued / InProgress, keep polling.
          }

          if (abortController.signal.aborted) return;
          const delay = reconcilePollDelayMs(Date.now() - startedAt);
          await new Promise((resolve) => {
            setTimeout(resolve, delay);
          });
        }
      } catch (err: unknown) {
        if (err instanceof Error && err.name === 'AbortError') {
          console.error('AI Advisory generation was cancelled by user', err);
        } else {
          const message = err instanceof Error ? err.message : 'Failed to send message';
          setError(message);
          callbacksRef.current?.onError?.(message);
        }
      } finally {
        setIsStreaming(false);
        abortControllerRef.current = null;

        await queryClient.invalidateQueries({ queryKey: ['ai-advisory-chat-history'] });
        await queryClient.invalidateQueries({ queryKey: ['ai-advisory-conversations'] });
      }
    }, []);

    return {
      sendStreamMessage,
      isStreaming,
      cancel,
      error,
      accumulatedText,
      resetAccumulatedText,
    };
  };

  const useDeleteConversation = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['delete-ai-advisory-conversation'],
      mutationFn: async (conversationId: string): Promise<void> => {
        // Temporary direct fetch until API schema is regenerated
        const response = await fetch(`/api/v1/ai-advisory/conversations/${conversationId}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth?.accessToken}`,
          },
        });

        if (!response.ok) {
          const errorText = await response.text().catch(() => response.statusText);
          throw new Error(`Delete failed: ${response.status} ${errorText}`);
        }
      },
      onSuccess: async () => {
        // Invalidate and refetch chat history and conversations to refresh the lists
        await queryClient.invalidateQueries({ queryKey: ['ai-advisory-chat-history'] });
        // Invalidate all conversation queries (with different page/pageSize combinations) and refetch
        await queryClient.invalidateQueries({
          queryKey: ['ai-advisory-conversations'],
          exact: false,
        });
        // Force refetch to ensure UI updates immediately
        await queryClient.refetchQueries({
          queryKey: ['ai-advisory-conversations'],
          exact: false,
        });
      },
    });

  // EWM-520 — rename an existing AI Advisory conversation. Server enforces owner-only
  // (matching tenant + user), 1–100 char title, and rejects whitespace-only.
  const useRenameConversation = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['rename-ai-advisory-conversation'],
      mutationFn: async ({
        conversationId,
        title,
      }: {
        conversationId: string;
        title: string;
      }): Promise<void> => {
        const response = await fetch(`/api/v1/ai-advisory/conversations/${conversationId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth?.accessToken}`,
          },
          body: JSON.stringify({ title }),
        });

        if (!response.ok) {
          const errorText = await response.text().catch(() => response.statusText);
          throw new Error(`Rename failed: ${response.status} ${errorText}`);
        }
      },
      // Optimistic update so the sidebar reflects the new title before the server round-trip,
      // then refetch on settle to reconcile with the server.
      onMutate: async ({ conversationId, title }) => {
        await queryClient.cancelQueries({
          queryKey: ['ai-advisory-conversations'],
          exact: false,
        });

        const previousQueries = queryClient.getQueriesData<GetConversationsResponse>({
          queryKey: ['ai-advisory-conversations'],
          exact: false,
        });

        for (const [queryKey, data] of previousQueries) {
          if (!data) continue;
          queryClient.setQueryData<GetConversationsResponse>(queryKey, {
            ...data,
            conversations: data.conversations.map((c) =>
              c.conversationId === conversationId ? { ...c, title } : c,
            ),
          });
        }

        return { previousQueries };
      },
      onError: (_error, _variables, context) => {
        const ctx = context as { previousQueries?: [unknown, unknown][] } | undefined;
        if (!ctx?.previousQueries) return;
        for (const [queryKey, data] of ctx.previousQueries) {
          queryClient.setQueryData(queryKey as readonly unknown[], data);
        }
      },
      onSettled: async () => {
        await queryClient.invalidateQueries({
          queryKey: ['ai-advisory-conversations'],
          exact: false,
        });
      },
    });

  // ---------------------------------------------------------------------
  // EWM-521: Conversation folder hooks
  // ---------------------------------------------------------------------

  const useGetConversationFolders = () =>
    useQuery({
      enabled: Boolean(auth),
      queryKey: ['ai-advisory-folders'],
      queryFn: async (): Promise<GetConversationFoldersResponse> => {
        const response = await fetch('/api/v1/ai-advisory/folders', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth?.accessToken}`,
          },
        });
        if (!response.ok) {
          throw new Error(`Failed to fetch folders: ${response.statusText}`);
        }
        return await response.json();
      },
    });

  const useCreateConversationFolder = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['create-ai-advisory-folder'],
      mutationFn: async ({ name }: { name: string }): Promise<CreateConversationFolderResponse> => {
        const response = await fetch('/api/v1/ai-advisory/folders', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth?.accessToken}`,
          },
          body: JSON.stringify({ name }),
        });
        if (!response.ok) {
          const errorText = await response.text().catch(() => response.statusText);
          throw new Error(`Create folder failed: ${response.status} ${errorText}`);
        }
        return await response.json();
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: ['ai-advisory-folders'] });
      },
    });

  const useRenameConversationFolder = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['rename-ai-advisory-folder'],
      mutationFn: async ({ folderId, name }: { folderId: string; name: string }): Promise<void> => {
        const response = await fetch(`/api/v1/ai-advisory/folders/${folderId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth?.accessToken}`,
          },
          body: JSON.stringify({ name }),
        });
        if (!response.ok) {
          const errorText = await response.text().catch(() => response.statusText);
          throw new Error(`Rename folder failed: ${response.status} ${errorText}`);
        }
      },
      // Optimistic rename so the sidebar updates without waiting for the round-trip,
      // then refetch on settle to reconcile with the server (incl. alphabetical re-sort).
      onMutate: async ({ folderId, name }) => {
        await queryClient.cancelQueries({ queryKey: ['ai-advisory-folders'] });
        const previous = queryClient.getQueryData<GetConversationFoldersResponse>([
          'ai-advisory-folders',
        ]);
        if (previous) {
          queryClient.setQueryData<GetConversationFoldersResponse>(['ai-advisory-folders'], {
            folders: previous.folders.map((f) => (f.folderId === folderId ? { ...f, name } : f)),
          });
        }
        return { previous };
      },
      onError: (_err, _vars, context) => {
        const ctx = context as { previous?: GetConversationFoldersResponse } | undefined;
        if (ctx?.previous) {
          queryClient.setQueryData(['ai-advisory-folders'], ctx.previous);
        }
      },
      onSettled: async () => {
        await queryClient.invalidateQueries({ queryKey: ['ai-advisory-folders'] });
      },
    });

  const useDeleteConversationFolder = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['delete-ai-advisory-folder'],
      mutationFn: async (folderId: string): Promise<void> => {
        const response = await fetch(`/api/v1/ai-advisory/folders/${folderId}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth?.accessToken}`,
          },
        });
        if (!response.ok) {
          const errorText = await response.text().catch(() => response.statusText);
          throw new Error(`Delete folder failed: ${response.status} ${errorText}`);
        }
      },
      onSuccess: async () => {
        // Folder list and conversation list both change (conversations are re-homed to top level).
        await queryClient.invalidateQueries({ queryKey: ['ai-advisory-folders'] });
        await queryClient.invalidateQueries({
          queryKey: ['ai-advisory-conversations'],
          exact: false,
        });
      },
    });

  const useMoveConversationToFolder = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['move-ai-advisory-conversation-to-folder'],
      mutationFn: async ({
        conversationId,
        folderId,
      }: {
        conversationId: string;
        folderId: string | null;
      }): Promise<void> => {
        const response = await fetch(`/api/v1/ai-advisory/conversations/${conversationId}/folder`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth?.accessToken}`,
          },
          body: JSON.stringify({ folderId }),
        });
        if (!response.ok) {
          const errorText = await response.text().catch(() => response.statusText);
          throw new Error(`Move conversation failed: ${response.status} ${errorText}`);
        }
      },
      // Optimistic move so the conversation jumps to the target folder immediately.
      onMutate: async ({ conversationId, folderId }) => {
        await queryClient.cancelQueries({
          queryKey: ['ai-advisory-conversations'],
          exact: false,
        });
        const previous = queryClient.getQueriesData<GetConversationsResponse>({
          queryKey: ['ai-advisory-conversations'],
          exact: false,
        });
        for (const [queryKey, data] of previous) {
          if (!data) continue;
          queryClient.setQueryData<GetConversationsResponse>(queryKey, {
            ...data,
            conversations: data.conversations.map((c) =>
              c.conversationId === conversationId ? { ...c, folderId } : c,
            ),
          });
        }
        return { previous };
      },
      onError: (_err, _vars, context) => {
        const ctx = context as { previous?: [unknown, unknown][] } | undefined;
        if (!ctx?.previous) return;
        for (const [queryKey, data] of ctx.previous) {
          queryClient.setQueryData(queryKey as readonly unknown[], data);
        }
      },
      onSettled: async () => {
        // Folder counts change too — invalidate both.
        await queryClient.invalidateQueries({
          queryKey: ['ai-advisory-conversations'],
          exact: false,
        });
        await queryClient.invalidateQueries({ queryKey: ['ai-advisory-folders'] });
      },
    });

  return {
    useCreateConversation,
    useSendMessage,
    useGetChatHistory,
    useGetAdvisoryStructure,
    useGetConversations,
    useExportConversation,
    useConvertConversationToEstimation,
    useGetAdvisoryConvertTargets,
    useStreamMessage,
    usePollMessage,
    useShareConversation,
    useDeleteConversation,
    useRenameConversation,
    useGetConversationFolders,
    useCreateConversationFolder,
    useRenameConversationFolder,
    useDeleteConversationFolder,
    useMoveConversationToFolder,
  };
};
